import { useState, useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, Download, ZoomIn, ZoomOut, Maximize2 } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
} from './ui/dialog';

export default function MediaPreviewModal({ 
  isOpen, 
  onClose, 
  media, 
  initialIndex = 0,
  onDownload 
}) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [zoom, setZoom] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    setCurrentIndex(initialIndex);
    setZoom(1);
  }, [initialIndex, isOpen]);

  const currentItem = media?.[currentIndex];
  const isVideo = currentItem?.content_type?.includes('video') || 
                  currentItem?.filename?.match(/\.(mp4|webm|mov)$/i);

  const goNext = useCallback(() => {
    if (currentIndex < media.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setZoom(1);
    }
  }, [currentIndex, media?.length]);

  const goPrev = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setZoom(1);
    }
  }, [currentIndex]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      switch (e.key) {
        case 'ArrowRight':
          goNext();
          break;
        case 'ArrowLeft':
          goPrev();
          break;
        case 'Escape':
          onClose();
          break;
        case '+':
        case '=':
          setZoom(z => Math.min(z + 0.25, 3));
          break;
        case '-':
          setZoom(z => Math.max(z - 0.25, 0.5));
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, goNext, goPrev, onClose]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const handleDownload = () => {
    if (onDownload && currentItem) {
      onDownload(currentItem);
    }
  };

  const getMediaUrl = (item) => {
    return item?.cloud_url || item?.url || item?.preview_url || item?.image_url || 
           `${process.env.REACT_APP_BACKEND_URL}/api/media/${item?.id}`;
  };

  if (!media || media.length === 0) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] max-h-[95vh] w-full h-full p-0 bg-black/95 border-none overflow-hidden">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-50 p-2 bg-black/50 rounded-full text-white hover:bg-black/80 transition-colors"
          data-testid="close-preview"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Navigation buttons */}
        {media.length > 1 && (
          <>
            <button
              onClick={goPrev}
              disabled={currentIndex === 0}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-50 p-3 bg-black/50 rounded-full text-white hover:bg-black/80 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="prev-media"
            >
              <ChevronLeft className="w-8 h-8" />
            </button>
            <button
              onClick={goNext}
              disabled={currentIndex === media.length - 1}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-50 p-3 bg-black/50 rounded-full text-white hover:bg-black/80 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="next-media"
            >
              <ChevronRight className="w-8 h-8" />
            </button>
          </>
        )}

        {/* Media content */}
        <div className="flex items-center justify-center w-full h-full overflow-hidden">
          {isVideo ? (
            <video
              src={getMediaUrl(currentItem)}
              className="max-w-full max-h-full"
              controls
              autoPlay
              playsInline
              webkit-playsinline="true"
              x5-playsinline="true"
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                minHeight: '200px'
              }}
            >
              <source src={getMediaUrl(currentItem)} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          ) : (
            <img
              src={getMediaUrl(currentItem)}
              alt={currentItem?.title || currentItem?.filename || 'Preview'}
              className="max-w-full max-h-full object-contain transition-transform duration-200"
              style={{ transform: `scale(${zoom})` }}
              draggable={false}
            />
          )}
        </div>

        {/* Bottom toolbar */}
        <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
          <div className="flex items-center justify-between max-w-4xl mx-auto">
            {/* Info */}
            <div className="text-white">
              <p className="font-medium truncate max-w-xs">
                {currentItem?.title || currentItem?.filename || 'Media'}
              </p>
              <p className="text-sm text-gray-400">
                {currentIndex + 1} van {media.length}
              </p>
            </div>

            {/* Controls */}
            <div className="flex items-center gap-2">
              {!isVideo && (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setZoom(z => Math.max(z - 0.25, 0.5))}
                    className="text-white hover:bg-white/20"
                    data-testid="zoom-out"
                  >
                    <ZoomOut className="w-5 h-5" />
                  </Button>
                  <span className="text-white text-sm min-w-[3rem] text-center">
                    {Math.round(zoom * 100)}%
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setZoom(z => Math.min(z + 0.25, 3))}
                    className="text-white hover:bg-white/20"
                    data-testid="zoom-in"
                  >
                    <ZoomIn className="w-5 h-5" />
                  </Button>
                </>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleFullscreen}
                className="text-white hover:bg-white/20"
                data-testid="fullscreen"
              >
                <Maximize2 className="w-5 h-5" />
              </Button>
              {onDownload && (
                <Button
                  onClick={handleDownload}
                  className="bg-[#D4AF37] hover:bg-[#B8963A] text-black"
                  data-testid="download-media"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Thumbnail strip */}
        {media.length > 1 && (
          <div className="absolute bottom-20 left-0 right-0 px-4">
            <div className="flex gap-2 justify-center overflow-x-auto max-w-full py-2">
              {media.slice(Math.max(0, currentIndex - 5), Math.min(media.length, currentIndex + 6)).map((item, i) => {
                const actualIndex = Math.max(0, currentIndex - 5) + i;
                return (
                  <button
                    key={item.id || actualIndex}
                    onClick={() => { setCurrentIndex(actualIndex); setZoom(1); }}
                    className={`w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 border-2 transition-all ${
                      actualIndex === currentIndex 
                        ? 'border-[#D4AF37] scale-110' 
                        : 'border-transparent opacity-60 hover:opacity-100'
                    }`}
                  >
                    <img
                      src={getMediaUrl(item)}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
